/**
 * <sticky-reserve-bar> — ports app/components/PdpMobileBar.tsx.
 *
 * Mobile-only sticky reserve bar. It stays off-screen until the in-page buy box
 * has scrolled above the fold (a sentinel rendered just below the reserve
 * button), then slides up; it retracts again when the footer comes into view so
 * it never overlaps it. Toggles `.is-visible` (base.css animates the slide) and
 * keeps aria-hidden in sync. Desktop hide is a base.css media query, so this
 * runs everywhere but is only ever visible on phones.
 *
 * Honors prefers-reduced-motion implicitly via the CSS transition.
 */
class StickyReserveBar extends HTMLElement {
  connectedCallback() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.pastBuyBox = false;
    this.footerVisible = false;
    this.observers = [];

    const sentinelId = this.getAttribute('data-sentinel');
    const sentinel = sentinelId ? document.getElementById(sentinelId) : null;
    const footer = document.querySelector('.site-footer') || document.querySelector('footer');

    if (sentinel) {
      const io = new IntersectionObserver(
        ([e]) => {
          // Past the buy box once the sentinel has scrolled above the viewport.
          this.pastBuyBox = !e.isIntersecting && e.boundingClientRect.top < 0;
          this.#update();
        },
        {threshold: 0},
      );
      io.observe(sentinel);
      this.observers.push(io);
    }

    if (footer) {
      const io = new IntersectionObserver(
        ([e]) => {
          this.footerVisible = e.isIntersecting;
          this.#update();
        },
        {threshold: 0},
      );
      io.observe(footer);
      this.observers.push(io);
    }
  }

  disconnectedCallback() {
    (this.observers || []).forEach((io) => io.disconnect());
  }

  #update() {
    const show = this.pastBuyBox && !this.footerVisible;
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
  }
}

if (!customElements.get('sticky-reserve-bar')) {
  customElements.define('sticky-reserve-bar', StickyReserveBar);
}
